// frontend/src/hooks/useMyTeamRestDayData.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from '../utils/toast';

/**
 * Manager-scoped ("My Team") rest-day interval monitoring data hook.
 *
 * Employee-facing twin of `pages/RestDayMonitoring/hooks/useRestDayData`.
 * Hits the `/staff-core/rest-day-monitoring/my-team/...` endpoints, which
 * return the SAME payload shape as the admin endpoints but scoped
 * server-side to the requesting RO / HOD's span of control.
 *
 * Differences from the admin hook:
 *   - No alerts / coverage / acknowledge (read-only, HR-only concerns).
 *   - Surfaces a `forbidden` flag on a 403 (user is not an RO/HOD).
 */
const useMyTeamRestDayData = () => {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const [filters, setFilters] = useState({
    status: '',
    department: '',
    search: '',
  });

  const fetchStats = useCallback(async () => {
    const response = await api.getMyTeamRestDayStats();
    setStats(response.data);
    return response.data;
  }, []);

  const fetchEmployees = useCallback(async (filterParams = {}) => {
    const params = {
      status: filterParams.status || filters.status,
      department: filterParams.department || filters.department,
      search: filterParams.search || filters.search,
    };
    Object.keys(params).forEach((key) => {
      if (!params[key]) delete params[key];
    });
    const response = await api.getMyTeamRestDayStatus(params);
    setEmployees(response.data.employees || []);
    return response.data;
  }, [filters]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchStats(), fetchEmployees()]);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
        setEmployees([]);
        setStats(null);
      } else {
        console.error('Error loading my-team rest day data:', err);
        setError(err.message || 'Failed to load rest day monitoring data');
        toast.error('Failed to load rest day monitoring data');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchEmployees]);

  const refetch = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const applyFilters = useCallback(async () => {
    setLoading(true);
    try {
      await fetchEmployees(filters);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error('Failed to apply filters');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, filters]);

  const getEmployeeDetail = useCallback(async (staffId) => {
    try {
      const response = await api.getRestDayEmployeeDetail(staffId);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day employee detail:', err);
      toast.error('Failed to load employee detail');
      return null;
    }
  }, []);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch employees when DB-level filters change (mirrors the admin hook).
  useEffect(() => {
    if (!loading && stats) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.department]);

  return {
    stats,
    employees,
    loading,
    error,
    forbidden,
    filters,
    refetch,
    updateFilters,
    applyFilters,
    getEmployeeDetail,
  };
};

export default useMyTeamRestDayData;
